"use client";

import React from "react";
import { Hexagon, Triangle, Circle, Square, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const steps = [{
  id: "01",
  title: "Diagnostic Co-Design",
  description: "We don't just audit your institution; we partner with you to understand the unique ecosystem of your campus. Together, we identify the hidden barriers to growth and the latent capabilities waiting to be unlocked.",
  icon: Hexagon,
  color: "text-secondary",
  shapeClass: "rotate-90"
}, {
  id: "02",
  title: "Strategic Blueprinting",
  description: "Moving beyond generic best practices, we co-create a tailored roadmap that aligns with your mission. This blueprint focuses on building sustainable internal structures rather than temporary fixes.",
  icon: Triangle,
  color: "text-chart-3",
  shapeClass: "rotate-180"
}, {
  id: "03",
  title: "Capability Embedding",
  description: "We work alongside your teams to implement the strategy. Our goal is to transfer knowledge and tools, ensuring your staff gains the expertise to manage and evolve the system independently.",
  icon: Square,
  color: "text-secondary",
  shapeClass: "rotate-45"
}, {
  id: "04",
  title: "Sustainable Evolution",
  description: "As the new ecosystem takes root, we step back. You own the growth, the data, and the future trajectory, equipped with a resilient framework that adapts to changing learner needs.",
  icon: Circle,
  color: "text-chart-3",
  shapeClass: ""
}];
const TimelineItem = ({
  step,
  index,
  isLast
}: {
  step: (typeof steps)[0];
  index: number;
  isLast: boolean;
}) => {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: false,
    threshold: 0.6
  });
  const Icon = step.icon;
  return <div ref={ref as React.RefObject<HTMLDivElement>} data-index={index} className={cn("relative flex gap-8 md:gap-12 transition-opacity duration-700 ease-in-out", isVisible ? "opacity-100" : "opacity-40")}>
      {}
      {!isLast && <div className="absolute left-[27px] top-16 bottom-[-4rem] w-px bg-border md:left-[35px]" />}

      {}
      <div className="flex flex-col items-center shrink-0">
        <div className={cn("relative flex items-center justify-center size-14 md:size-18 bg-background border border-border rounded-full shadow-sm z-10", step.color)}>
          <Icon className={cn("size-6 md:size-8 fill-current/10", step.shapeClass)} strokeWidth={1.5} />
          <span className="absolute text-[10px] font-bold tracking-tighter text-foreground/70 font-mono mt-0.5">
            {step.id}
          </span>
        </div>
      </div>

      {}
      <div className="pb-16 pt-2 md:pt-4 max-w-2xl">
        <h3 className="text-2xl font-serif font-medium mb-4 !text-[hsl(210,14%,83%)] md:text-3xl">
          {step.title}
        </h3>
        <p className="text-lg leading-relaxed !text-[hsl(0,0%,100%)]">
          {step.description}
        </p>
      </div>
    </div>;
};
export default function OurProcess() {
  return <section id="our-process" className="py-24 bg-background relative overflow-hidden bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1443870/img/strategy_map_blueprint.png)] bg-cover bg-center bg-no-repeat !text-transparent">
      <div className="absolute inset-0 bg-black/30 pointer-events-none" />
      {}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-muted/30 to-transparent pointer-events-none" />
      
      <div className="container mx-auto px-6 md:px-12 relative z-10">
        <div className="max-w-3xl mb-20">
          <h2 className="text-sm font-bold tracking-widest text-secondary uppercase mb-4">
            How We Work
          </h2>
          <h3 className="text-4xl font-serif mb-6 !text-[hsl(210,11%,71%)] md:text-5xl">
            A Partnership, Not a Handover.
          </h3>
          <p className="text-xl max-w-2xl !text-[hsl(0,0%,100%)]">Our process is designed to build your capacity, not your dependency. We move from diagnosis to sustainable ownership in four pragmatic steps.</p>
        </div>

        <div className="flex flex-col ml-0 md:ml-8">
          {steps.map((step, i) => <TimelineItem key={step.id} step={step} index={i} isLast={i === steps.length - 1} />)}
        </div>

        <div className="mt-8 ml-0 md:ml-8 pl-0 md:pl-[calc(4.5rem+3rem)]">
          <Button asChild size="lg" className="uppercase tracking-wider font-bold">
            <Link to="https://edustrategygroup.com/schedule-your-strategy-session/">
              Start a Conversation <ArrowRight className="ml-2 size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}